import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import type { CheckoutSession } from '../api';
import Countdown from './Countdown';
import StatusBadge from './StatusBadge';

interface DepositScreenProps {
  session: CheckoutSession;
  onExpire: () => void;
  onCancel: () => void;
  cancelling: boolean;
}

/**
 * Renders an integer base-unit amount as a human decimal string.
 *
 * BigInt, never Number: an 18-decimal chain exceeds MAX_SAFE_INTEGER, so
 * parseFloat silently corrupts the very number a player is about to send.
 * Returns null when there is nothing sensible to show, and the caller
 * falls back to asking for any amount.
 */
function formatAmount(raw: string | undefined, decimals: number): string | null {
  if (!raw) return null;
  let v: bigint;
  try {
    v = BigInt(raw);
  } catch {
    return null;
  }
  if (decimals <= 0) return v.toString();
  const base = 10n ** BigInt(decimals);
  const whole = v / base;
  const frac = (v % base).toString().padStart(decimals, '0').replace(/0+$/, '');
  return frac ? `${whole}.${frac}` : whole.toString();
}

export default function DepositScreen({
  session,
  onExpire,
  onCancel,
  cancelling,
}: DepositScreenProps) {
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const address = session.address;

  useEffect(() => {
    if (!address) {
      setQrDataUrl(null);
      return;
    }
    let cancelled = false;
    QRCode.toDataURL(address, { errorCorrectionLevel: 'M', margin: 1, width: 220 })
      .then((url) => {
        if (!cancelled) setQrDataUrl(url);
      })
      .catch(() => {
        if (!cancelled) setQrDataUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [address]);

  // This used to `throw` when the address was missing. With no error
  // boundary in the tree, that unmounted the whole app and left a blank
  // white page — the least diagnosable failure available. Render the
  // problem instead; App only routes here once needsMethod() is false, so
  // reaching this branch means something upstream is genuinely wrong.
  if (!address) {
    return (
      <div className="card">
        <h2 className="heading">Payment address unavailable</h2>
        <p className="error">
          This checkout has no deposit address yet. Please close this window and
          start the payment again.
        </p>
        <button type="button" className="button secondary" onClick={onCancel}>
          Close
        </button>
      </div>
    );
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked — the address block is user-select: all, so it
      // can still be long-pressed or tapped to select.
    }
  };

  const amount = formatAmount(session.expected_amount, session.asset_decimals);
  const unit = session.asset_symbol || session.asset_id || '';

  return (
    <div className="card">
      <div className="status-row">
        <StatusBadge status={session.status} />
        <Countdown expiresAt={session.expires_at} onExpire={onExpire} />
      </div>
      <h2 className="heading">
        {amount ? `Send ${amount} ${unit}` : `Send ${unit}`}
      </h2>
      <p className="subheading">to this {session.chain_id} address:</p>
      {qrDataUrl ? (
        <img src={qrDataUrl} className="qr" alt="Deposit address QR code" />
      ) : (
        <div className="qr-placeholder" aria-hidden="true" />
      )}
      <div className="address-block">{address}</div>
      {/* Chains that use one treat a missing memo as an unattributable
          payment, so it is shown as prominently as the address itself. */}
      {session.memo && (
        <p className="subheading">
          Include memo / tag: <span className="address-block">{session.memo}</span>
        </p>
      )}
      <button type="button" className="button primary" onClick={handleCopy}>
        {copied ? 'Copied!' : 'Copy address'}
      </button>
      <button
        type="button"
        className="button secondary"
        onClick={onCancel}
        disabled={cancelling}
      >
        {cancelling ? 'Cancelling…' : 'Cancel payment'}
      </button>
      <p className="muted center">
        This page will confirm automatically once the payment is detected
        on-chain.
      </p>
    </div>
  );
}
